import { isIncomingStockTargetRefreshing, INVENTORY_PLANNER_REFRESH_KEY } from './cacheRefreshStatus';
import { getShopifyStoreDomainForRefreshTarget } from '../data/refreshTargetDetails';

const AU_TZ = 'Australia/Sydney';

export function formatRefreshTimestamp(value) {
  if (value == null || value === '') return null;
  const ms = typeof value === 'number' ? value : Date.parse(value);
  if (!Number.isFinite(ms) || ms <= 0) return null;
  return new Date(ms).toLocaleString('en-AU', {
    timeZone: AU_TZ,
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
}

export function formatRefreshLastRunLabel(value) {
  const formatted = formatRefreshTimestamp(value);
  return formatted ? formatted : 'Never run';
}

/** Short operator-friendly error text (Firestore index URLs etc.). */
export function formatRefreshErrorSummary(errorText) {
  const raw = String(errorText || '').trim();
  if (!raw) return null;
  if (/requires an index/i.test(raw)) {
    if (/currently building/i.test(raw)) {
      return 'Firestore index still building — wait a few minutes, then retry.';
    }
    return 'Firestore index missing — deploy indexes or open the Firebase console link from server logs.';
  }
  if (raw.length <= 140) return raw;
  return `${raw.slice(0, 137)}…`;
}

function pickLatestTimestamp(...values) {
  const parsed = values
    .map((value) => {
      if (value == null || value === '') return null;
      if (typeof value === 'number') return value;
      const ms = Date.parse(value);
      return Number.isFinite(ms) ? ms : null;
    })
    .filter((ms) => ms != null);
  return parsed.length ? Math.max(...parsed) : null;
}

function findPlannerSyncJob(plannerSyncJobs, channel) {
  return (plannerSyncJobs || []).find(
    (job) => String(job.channel || '').trim() === channel
  ) || null;
}

function isTargetRunningInPicker(targetKey, {
  cacheStatus,
  refreshTargetStatuses,
  isRefreshTargetInCurrentRun,
  plannerSyncJobs,
  plannerCostBackfillStatus,
  plannerSupplierSkuSyncStatus
}) {
  if (isRefreshTargetInCurrentRun(targetKey)) {
    const runStatus = refreshTargetStatuses?.[targetKey];
    if (runStatus === 'refreshing' || runStatus === 'started_background' || runStatus === 'queued') {
      return true;
    }
  }

  const shopifyDomain = getShopifyStoreDomainForRefreshTarget(targetKey);
  if (shopifyDomain) {
    const store = cacheStatus?.stores?.find((entry) => entry.store === shopifyDomain);
    return !!(store?.isRefreshing || store?.isPending);
  }

  if (targetKey === 'purchase-orders-phaser') {
    return isRefreshTargetInCurrentRun(targetKey)
      && (refreshTargetStatuses?.[targetKey] === 'refreshing'
        || (refreshTargetStatuses?.[targetKey] === 'queued'
          && isIncomingStockTargetRefreshing(cacheStatus, 'phaser')));
  }

  if (targetKey === 'purchase-orders-ccc') {
    return isRefreshTargetInCurrentRun(targetKey)
      && (refreshTargetStatuses?.[targetKey] === 'refreshing'
        || (refreshTargetStatuses?.[targetKey] === 'queued'
          && isIncomingStockTargetRefreshing(cacheStatus, 'ccc')));
  }

  if (targetKey === 'qoblox-suppliers') {
    return isRefreshTargetInCurrentRun(targetKey)
      && (refreshTargetStatuses?.[targetKey] === 'refreshing'
        || (refreshTargetStatuses?.[targetKey] === 'queued'
          && isIncomingStockTargetRefreshing(cacheStatus, 'suppliers')));
  }

  if (targetKey === INVENTORY_PLANNER_REFRESH_KEY) {
    return !!(isRefreshTargetInCurrentRun(targetKey) && cacheStatus?.plannerRefresh?.isRefreshing);
  }

  if (targetKey === 'planner-catalog-ccc') {
    return !!(isRefreshTargetInCurrentRun(targetKey) && cacheStatus?.plannerCatalogRefresh?.isRefreshing);
  }

  if (targetKey === 'planner-sales-phaser') {
    const job = findPlannerSyncJob(plannerSyncJobs, 'phaserfpv');
    return String(job?.status || '').trim() === 'running' || job?.inProcess === true;
  }

  if (targetKey === 'planner-sales-ccc') {
    const job = findPlannerSyncJob(plannerSyncJobs, 'centralcoastcoffee');
    return String(job?.status || '').trim() === 'running' || job?.inProcess === true;
  }

  if (targetKey === 'planner-cost-backfill') {
    return plannerCostBackfillStatus?.jobStatus === 'running' || plannerCostBackfillStatus?.inProcess === true;
  }

  if (targetKey === 'planner-supplier-skus') {
    return plannerSupplierSkuSyncStatus?.jobStatus === 'running' || plannerSupplierSkuSyncStatus?.inProcess === true;
  }

  return false;
}

/**
 * Unified refresh picker status for one target.
 * @returns {{ phase: string, phaseLabel: string, lastRunMs: number|null, lastRunText: string, summaryText: string, errorText: string|null }}
 */
export function resolveRefreshTargetDisplay(targetKey, context = {}) {
  const {
    cacheStatus,
    plannerSyncJobs,
    plannerCostBackfillStatus,
    plannerSupplierSkuSyncStatus
  } = context;

  const running = isTargetRunningInPicker(targetKey, context);
  let lastRunMs = null;
  let summaryText = '';
  let errorText = null;
  let phase = 'ready';
  let phaseLabel = 'Ready';

  const shopifyDomain = getShopifyStoreDomainForRefreshTarget(targetKey);
  if (shopifyDomain) {
    const store = cacheStatus?.stores?.find((entry) => entry.store === shopifyDomain);
    lastRunMs = pickLatestTimestamp(store?.lastRefresh);
    summaryText = `${store?.productCount ?? 0} products in cache`;
    if (store?.error) {
      errorText = String(store.error);
      phase = 'error';
      phaseLabel = 'Error';
    }
  } else if (targetKey === 'purchase-orders-phaser') {
    const row = cacheStatus?.poCache?.phaser;
    lastRunMs = pickLatestTimestamp(row?.lastFetch);
    summaryText = `${row?.orderCount ?? 0} POs in cache`;
    if (row?.lastError) {
      errorText = String(row.lastError);
      phase = 'error';
      phaseLabel = 'Error';
    }
  } else if (targetKey === 'purchase-orders-ccc') {
    const row = cacheStatus?.poCache?.ccc;
    lastRunMs = pickLatestTimestamp(row?.lastFetch);
    summaryText = `${row?.orderCount ?? 0} POs in cache`;
    if (row?.lastError) {
      errorText = String(row.lastError);
      phase = 'error';
      phaseLabel = 'Error';
    }
  } else if (targetKey === 'qoblox-suppliers') {
    const suppliers = cacheStatus?.qobloxSuppliers;
    if (!suppliers?.configured) {
      return {
        phase: 'warning',
        phaseLabel: 'Not configured',
        lastRunMs: null,
        lastRunText: 'Never run',
        summaryText: 'QOBLEX_API_KEY not set',
        errorText: suppliers?.reason || null
      };
    }
    lastRunMs = pickLatestTimestamp(suppliers?.syncedAt);
    summaryText = `${suppliers?.count ?? 0} suppliers mapped`;
    if (suppliers?.isStale) {
      phase = 'warning';
      phaseLabel = 'Stale';
    }
    if (suppliers?.error) {
      errorText = String(suppliers.error);
      phase = 'error';
      phaseLabel = 'Error';
    }
  } else if (targetKey === INVENTORY_PLANNER_REFRESH_KEY) {
    const row = cacheStatus?.plannerRefresh;
    lastRunMs = pickLatestTimestamp(row?.lastAttempt);
    const durationSec = row?.lastDurationMs ? Math.round(row.lastDurationMs / 1000) : null;
    summaryText = durationSec != null
      ? `Last cycle took ${durationSec}s (forecast + buy lists)`
      : 'Forecast + buy lists recalculation';
    if (row?.lastError) {
      errorText = String(row.lastError);
      phase = 'error';
      phaseLabel = 'Error';
    }
  } else if (targetKey === 'planner-catalog-ccc') {
    const row = cacheStatus?.plannerCatalogRefresh;
    lastRunMs = pickLatestTimestamp(row?.lastAttempt);
    const durationSec = row?.lastDurationMs ? Math.round(row.lastDurationMs / 1000) : null;
    summaryText = durationSec != null
      ? `Last catalog sync took ${durationSec}s`
      : 'Shopify cache → planner offers + SKUs';
    if (row?.lastError) {
      errorText = String(row.lastError);
      phase = 'error';
      phaseLabel = 'Error';
    }
  } else if (targetKey === 'planner-sales-phaser') {
    const job = findPlannerSyncJob(plannerSyncJobs, 'phaserfpv');
    lastRunMs = pickLatestTimestamp(job?.lastSuccessfulAt, job?.completedAt, job?.updatedAt, job?.startedAt);
    const syncStatus = String(job?.status || 'idle').trim() || 'idle';
    const orders = Number(job?.ordersFetched);
    summaryText = Number.isFinite(orders) && orders > 0
      ? `Full ingest · ${orders.toLocaleString('en-AU')} orders synced · status ${syncStatus}`
      : `Full ingest · status ${syncStatus}`;
    if (job?.lastFailedError) errorText = String(job.lastFailedError);
    if (syncStatus === 'failed' || syncStatus === 'interrupted') {
      phase = 'error';
      phaseLabel = syncStatus === 'interrupted' ? 'Interrupted' : 'Failed';
    } else if (syncStatus === 'paused') {
      phase = 'warning';
      phaseLabel = 'Paused';
    }
  } else if (targetKey === 'planner-sales-ccc') {
    const job = findPlannerSyncJob(plannerSyncJobs, 'centralcoastcoffee');
    lastRunMs = pickLatestTimestamp(job?.lastSuccessfulAt, job?.completedAt, job?.updatedAt, job?.startedAt);
    const syncStatus = String(job?.status || 'idle').trim() || 'idle';
    const orders = Number(job?.ordersFetched);
    summaryText = Number.isFinite(orders) && orders > 0
      ? `Full ingest · ${orders.toLocaleString('en-AU')} orders synced · status ${syncStatus}`
      : `Full ingest · status ${syncStatus}`;
    if (job?.lastFailedError) errorText = String(job.lastFailedError);
    if (syncStatus === 'failed' || syncStatus === 'interrupted') {
      phase = 'error';
      phaseLabel = syncStatus === 'interrupted' ? 'Interrupted' : 'Failed';
    } else if (syncStatus === 'paused') {
      phase = 'warning';
      phaseLabel = 'Paused';
    }
  } else if (targetKey === 'planner-cost-backfill') {
    const row = plannerCostBackfillStatus || {};
    lastRunMs = pickLatestTimestamp(row.completedAt, row.jobUpdatedAt, row.jobStartedAt);
    const processed = Number(row.processedCount ?? row.processed);
    summaryText = Number.isFinite(processed) && processed > 0
      ? `Cost sync · ${processed.toLocaleString('en-AU')} SKUs processed`
      : 'Lightspeed average cost → planner offers';
    const jobStatus = String(row.jobStatus || 'idle').trim() || 'idle';
    if (jobStatus === 'failed') {
      phase = 'error';
      phaseLabel = 'Failed';
      if (row.lastError) errorText = String(row.lastError);
    } else if (jobStatus === 'paused') {
      phase = 'warning';
      phaseLabel = 'Paused';
    }
  } else if (targetKey === 'planner-supplier-skus') {
    const row = plannerSupplierSkuSyncStatus || cacheStatus?.plannerSupplierSkuSync || {};
    lastRunMs = pickLatestTimestamp(row.completedAt, row.jobUpdatedAt, row.jobStartedAt);
    const offersWithCode = Number(row.stats?.offersWithCode);
    const offersWritten = Number(row.stats?.offersWritten);
    summaryText = Number.isFinite(offersWithCode) && offersWithCode > 0
      ? `Supplier codes · ${offersWithCode.toLocaleString('en-AU')} codes on ${offersWritten.toLocaleString('en-AU')} offers`
      : 'Lightspeed supplier codes → planner_supplier_offers.supplierSku';
    const jobStatus = String(row.jobStatus || 'idle').trim() || 'idle';
    if (jobStatus === 'failed' || jobStatus === 'interrupted') {
      phase = 'error';
      phaseLabel = jobStatus === 'interrupted' ? 'Interrupted' : 'Failed';
      if (row.jobLastError) errorText = String(row.jobLastError);
    } else if (jobStatus === 'paused') {
      phase = 'warning';
      phaseLabel = 'Paused';
    }
  }

  if (running) {
    phase = 'running';
    phaseLabel = 'Running';
  } else if (!lastRunMs && phase === 'ready') {
    phase = 'never';
    phaseLabel = 'Not run yet';
  }

  const lastRunText = running
    ? (lastRunMs ? `Previous run ${formatRefreshLastRunLabel(lastRunMs)}` : 'Running now')
    : (lastRunMs ? `Last run ${formatRefreshLastRunLabel(lastRunMs)}` : 'Never run');

  return {
    phase,
    phaseLabel,
    lastRunMs,
    lastRunText,
    summaryText,
    errorText
  };
}
